import React, { useState, useEffect, useContext, useCallback } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import {
  Box,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  CircularProgress
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MUIDataTable, { MUIDataTableColumnOptions, SelectableRows } from 'mui-datatables';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import FileUploadRow from '../../_shared/FileUploadRow';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { LegalDocumentService } from '../../../services/LegalDocumentService';
import { LegalDocumentType } from '../../../interfaces/LegalDocument';
import { IReferential } from '../../../interfaces/IReferential';
import useConnectAppTypeId from '../../../hooks/useConnectAppTypeId';
import moment from 'moment';
import { ScaleLoader } from 'react-spinners';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { openFileNewTab } from '../../../helpers/file.utils';

interface LegalDocumentsPageProps {
  appState: AppState;
}

type Props = LegalDocumentsPageProps & ProviderContext;

const LegalDocuments: React.FC<Props> = (props) => {
  const context = useContext(ApplicationContext);
  const translatorService: ITranslatorService = (context as AppContext).translatorService;
  const legalDocumentService: LegalDocumentService = (context as AppContext).legalDocumentService;

  const connectAppTypeId = useConnectAppTypeId();

  const [documents, setDocuments] = useState<LegalDocumentType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [documentTypes, setDocumentTypes] = useState<IReferential[]>([]);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newDocVersion, setNewDocVersion] = useState('');
  const [newDocType, setNewDocType] = useState<IReferential | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewingFileName, setPreviewingFileName] = useState<string | null>(null);
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(null);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const types = await legalDocumentService.GetLegalDocumentTypes();
      setDocumentTypes(Array.isArray(types) ? types : Object.values(types));
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENT_TYPES_ERROR_TEXT',
          'Error fetching document types'
        ),
        { variant: 'error' }
      );
    }
  }, [legalDocumentService, props, translatorService]);

  const loadDocuments = useCallback(async () => {
    if (connectAppTypeId === null) return;

    setIsLoading(true);
    try {
      const response = await legalDocumentService.GetAllLegalDocuments({
        page: page + 1,
        pageSize,
        fleetLegalDocumentTypeId: selectedDocumentTypeId,
        appTypeId: connectAppTypeId
      });
      setDocuments(response.legalDocuments || []);
      setTotalCount(response.total || 0);
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENTS_ERROR_TEXT',
          'Error fetching legal documents'
        ),
        { variant: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    connectAppTypeId,
    page,
    pageSize,
    selectedDocumentTypeId,
    legalDocumentService,
    props,
    translatorService
  ]);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDownload = async (fileName: string, originalFileName: string) => {
    setDownloadingFileName(fileName);
    try {
      const blob = await legalDocumentService.DownloadLegalDocument(fileName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalFileName || `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate('FLEET_ERROR_DOWNLOADING_FILE_TEXT', 'Error downloading file'),
        { variant: 'error' }
      );
    } finally {
      setDownloadingFileName(null);
    }
  };

  const handlePreview = async (fileName: string, originalFileName: string) => {
    setPreviewingFileName(fileName);
    try {
      const blob = await legalDocumentService.DownloadLegalDocument(fileName);
      const ext =
        originalFileName && originalFileName.includes('.')
          ? originalFileName.split('.').pop() || 'pdf'
          : 'pdf';
      openFileNewTab(blob, ext);
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate('FLEET_DOCUMENT_FAILED_TO_LOAD', 'Failed to load document'),
        { variant: 'error' }
      );
    } finally {
      setPreviewingFileName(null);
    }
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
    setNewDocVersion('');
    setNewDocType(null);
    setSelectedFile(null);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setNewDocVersion('');
    setNewDocType(null);
    setSelectedFile(null);
  };

  const handleSubmitDocument = async () => {
    if (!selectedFile) {
      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENT_FILE_REQUIRED',
          'Please select a PDF file'
        ),
        { variant: 'warning' }
      );
      return;
    }

    if (!newDocType) {
      props.enqueueSnackbar(translatorService.Tranlate('VALIDATORS_REQUIRED', 'Mandatory field'), {
        variant: 'warning'
      });
      return;
    }

    if (!connectAppTypeId) {
      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_APP_TYPE_NOT_LOADED',
          'App type not loaded yet, please try again'
        ),
        { variant: 'warning' }
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('FleetLegalDocumentTypeId', newDocType.id.toString());
      formData.append('Version', newDocVersion);
      formData.append('FileName', selectedFile.name);
      formData.append('OriginalFileName', selectedFile.name);
      formData.append('file', selectedFile);
      formData.append('appTypeId', connectAppTypeId.toString());

      await legalDocumentService.CreateLegalDocument(formData);

      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENT_UPLOAD_SUCCESS',
          'Document uploaded successfully!'
        ),
        { variant: 'success' }
      );

      handleCloseAddDialog();
      await loadDocuments();
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENT_CREATE_ERROR',
          'An error occurred while uploading the document'
        ),
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getColumns = () => {
    return [
      {
        name: 'uploadDate',
        label: translatorService.Tranlate('FLEET_LEGAL_DOCUMENTS_UPLOAD_DATE_LABEL', 'Upload date'),
        options: {
          filter: false,
          customBodyRender: (value: string) =>
            value
              ? moment
                  .utc(value)
                  .local()
                  .format(props.appState.longDateFormat || 'DD.MM.YYYY HH:mm')
              : '-'
        } as MUIDataTableColumnOptions
      },
      {
        name: 'fleetLegalDocumentType',
        label: translatorService.Tranlate('FLEET_LEGAL_DOCUMENT_TYPE', 'Document type'),
        options: {
          filter: true,
          filterType: 'dropdown',
          filterList: selectedDocumentTypeId
            ? [
                (() => {
                  const match = documentTypes.find((dt) => dt.id === selectedDocumentTypeId);
                  return match ? match.displayName || match.name || '' : '';
                })()
              ]
            : [],
          filterOptions: {
            names: documentTypes.map((dt) => dt.displayName || dt.name || '')
          },
          customBodyRender: (value: any) => value?.displayName || value?.name || '-'
        } as MUIDataTableColumnOptions
      },
      {
        name: 'version',
        label: translatorService.Tranlate('FLEET_LEGAL_DOCUMENT_VERSION_LABEL', 'Version'),
        options: {
          filter: false,
          customBodyRender: (value: string | null) => value || '-'
        } as MUIDataTableColumnOptions
      },
      {
        name: 'originalFileName',
        label: translatorService.Tranlate('FLEET_LEGAL_DOCUMENTS_FILE_NAME_LABEL', 'File name'),
        options: {
          filter: false,
          customBodyRender: (value: string) => value || '-'
        } as MUIDataTableColumnOptions
      },
      {
        name: 'fileName',
        label: translatorService.Tranlate('FLEET_LEGAL_DOCUMENTS_ACTIONS_LABEL', 'Actions'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: string, tableMeta: any) => {
            const originalFileName = tableMeta.rowData[3] || '';
            return (
              <div>
                <Tooltip
                  title={translatorService.Tranlate('FLEET_DOWNLOAD_TOOLTIP_TEXT', 'Download')}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={downloadingFileName === value}
                    onClick={() => handleDownload(value, originalFileName)}
                  >
                    {downloadingFileName === value ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <GetAppIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={translatorService.Tranlate('FLEET_PREVIEW_TOOLTIP_TEXT', 'Preview')}
                >
                  <IconButton
                    size="small"
                    color="secondary"
                    disabled={previewingFileName === value}
                    onClick={() => handlePreview(value, originalFileName)}
                  >
                    {previewingFileName === value ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            );
          }
        } as MUIDataTableColumnOptions
      }
    ];
  };

  const options = {
    filter: true,
    filterType: 'dropdown' as const,
    search: false,
    selectableRows: 'none' as SelectableRows,
    selectableRowsOnClick: false,
    print: false,
    download: false,
    serverSide: true,
    count: totalCount,
    page: page,
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 25, 50],
    onChangePage: (newPage: number) => setPage(newPage),
    onChangeRowsPerPage: (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(0);
    },
    onFilterChange: (_changedColumn: string, filterList: string[][]) => {
      const docTypeColIndex = 1;
      const selectedName = filterList[docTypeColIndex]?.[0] || null;

      if (selectedName) {
        const match = documentTypes.find(
          (dt) => (dt.displayName || dt.name || '') === selectedName
        );
        setSelectedDocumentTypeId(match ? match.id : null);
      } else {
        setSelectedDocumentTypeId(null);
      }
      setPage(0);
    },
    textLabels: MUITranslations.GetTranslations(translatorService),
    customToolbar: () => {
      return (
        <Tooltip
          title={translatorService.Tranlate('FLEET_ADD_LEGAL_DOCUMENT_TEXT', 'Add legal document')}
        >
          <IconButton
            aria-label="add"
            color="secondary"
            className="m-2"
            onClick={handleOpenAddDialog}
          >
            <AddCircleTwoToneIcon />
          </IconButton>
        </Tooltip>
      );
    }
  };

  return (
    <React.Fragment>
      <Box m={1} p={1}>
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={isLoading} />
        </div>

        {!isLoading ? (
          <MUIDataTable
            title={translatorService.Tranlate(
              'FLEET_LEGAL_DOCUMENTS_PAGE_TITLE_TEXT',
              'Legal documents'
            )}
            data={documents}
            columns={getColumns()}
            options={options}
          />
        ) : null}
      </Box>

      {/* Add Legal Document Dialog */}
      <Dialog
        onClose={handleCloseAddDialog}
        aria-labelledby="add-legal-document-dialog"
        open={isAddDialogOpen}
        fullWidth={true}
        maxWidth={'sm'}
      >
        <DialogTitle id="add-legal-document-dialog">
          <Grid container>
            <Grid item xs={11}>
              <Typography variant="h4">
                {translatorService.Tranlate(
                  'FLEET_ADD_LEGAL_DOCUMENT_MODAL_TITLE',
                  'Add legal document'
                )}
              </Typography>
            </Grid>
            <Grid item xs={1}>
              <IconButton aria-label="close" onClick={handleCloseAddDialog} size={'small'}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          <ValidatorForm onSubmit={handleSubmitDocument} instantValidate={true}>
            <Box mb={2}>
              <Autocomplete
                id="documentType"
                fullWidth
                options={documentTypes}
                value={newDocType}
                onChange={(_e: any, newValue: IReferential | null) => setNewDocType(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || option.name || ''}
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="documentType"
                    value={newDocType}
                    label={translatorService.Tranlate('FLEET_LEGAL_DOCUMENT_TYPE', 'Document type')}
                    fullWidth
                    validators={['required']}
                    errorMessages={[
                      translatorService.Tranlate('VALIDATORS_REQUIRED', 'Field is required')
                    ]}
                  />
                )}
              />
            </Box>

            <Box mb={2}>
              <TextValidator
                fullWidth
                name="version"
                id="version"
                value={newDocVersion}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewDocVersion(e.target.value)
                }
                label={translatorService.Tranlate('FLEET_LEGAL_DOCUMENT_VERSION_LABEL', 'Version')}
                validators={['required']}
                errorMessages={[
                  translatorService.Tranlate('VALIDATORS_REQUIRED', 'Field is required')
                ]}
              />
            </Box>

            <Box className="m-2" mt={2}>
              <FileUploadRow
                inputId="legal-document-file"
                title={translatorService.Tranlate(
                  'FLEET_LEGAL_DOCUMENT_ADD_FILE',
                  'Add file'
                )}
                emptyDescription={translatorService.Tranlate(
                  'FLEET_LEGAL_DOCUMENT_ADD_FILE_DESC',
                  'Select a .pdf file'
                )}
                uploadButtonLabel={translatorService.Tranlate(
                  'FLEET_UPLOAD_FILE_BUTTON_LABEL',
                  'Upload file'
                )}
                accept=".pdf"
                fileName={selectedFile?.name ?? null}
                onFileSelected={(file: File) => setSelectedFile(file)}
                onFileRemoved={() => setSelectedFile(null)}
              />
            </Box>

            <div className="text-right">
              <Button
                className="m-2"
                variant="outlined"
                color="default"
                onClick={handleCloseAddDialog}
              >
                {translatorService.Tranlate('FLEET_CANCEL_BUTTON_LABEL', 'Cancel')}
              </Button>
              <Button
                className="m-2"
                variant="contained"
                color="secondary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  translatorService.Tranlate('FLEET_UPLOAD_DOCUMENT_BUTTON', 'Upload document')
                )}
              </Button>
            </div>
          </ValidatorForm>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(LegalDocuments as any));
